import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Auth } from '@aws-amplify/auth';
import { getS3TimeKeyPrefix, S3TimeKeyLevel } from '@iotv/datamodel-core';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { listAllKeys } from '@iotv/s3';
import { Alert, Typography } from '@mui/material';
import { Stack } from '@mui/material';
import { JsonViewer } from '@dataxpdtn/mui-json-viewer';
import { serviceDefinition } from 'internal-config';
import { useContext, useEffect, useReducer } from 'react';
import { getObjectFromS3 } from '../../data/aws/s3/api';
import { PeriodRecent, useModularTimePeriodSelector } from '../../hooks/useModularTimePeriodSelector';
import { MultiReportStatus } from './MulitReportStatus';
import { getDefaultState, SystemSelfCheckContext, SystemSelfCheckReducer } from './context';
import { SystemReportTimeKeyToTimestamp } from './fns';
import { IoTVAccordion, IoTVAccordionDetails, IoTVAccordionSummary } from '../common/IoTVAccordion';
const debug = process.env.REACT_APP_DEBUG && false;
const Bucket = serviceDefinition.S3.serviceDataBucket.name;
const Prefix = `system/operation`;
const timeKeyRegexOut = new RegExp(`${Prefix}/`);
const defaultState = getDefaultState();
export const SystemSelfCheckView = ({}) => {
    const [state, dispatch] = useReducer(SystemSelfCheckReducer, defaultState);
    return (_jsx(SystemSelfCheckContext.Provider, { value: { state, dispatch }, children: _jsx(SystemSelfCheckViewInternal, {}) }));
};
export const SystemSelfCheckViewInternal = ({}) => {
    const { state: { systemOperationRecords, timeFilteredReports, error }, dispatch, } = useContext(SystemSelfCheckContext);
    const { components: { TimePeriodSelector }, fromDateValue, toDateValue, } = useModularTimePeriodSelector({
        rangeUnit: 'hours',
        label: 'System Self Check (dates)',
        initialPeriodRecent: PeriodRecent.RECENT,
        rangeLength: 24,
        requireConfirmation: true,
    });
    //Initial call to get all keys, once
    const getKeys = async () => {
        const credentials = await Auth.currentCredentials();
        try {
            const keys = await listAllKeys({ Bucket, Prefix, credentials });
            dispatch({ systemOperationRecords: Object.fromEntries(keys.sort((x, y) => (x > y ? -1 : 1)).map((k) => [k, null])), error: null });
            debug && console.log('dispatched', keys);
        }
        catch (e) {
            debug && console.log('Error getting keys', e.message);
            dispatch({ error: 'Unable to access System Self Check reports' });
        }
    };
    // when Dates change and keys are present filter for display, having first retrieved any null entry values
    const filterKeys = async () => {
        try {
            const fromTimeKeyPrefix = getS3TimeKeyPrefix({ timestamp: fromDateValue.unix() * Milliseconds.SECOND, s3TimeKeyLevel: S3TimeKeyLevel.HOUR });
            const endOfToDatePeriod = toDateValue.add(1, 'hour').endOf('hour');
            const toTimeKeyPrefix = getS3TimeKeyPrefix({ timestamp: endOfToDatePeriod.unix() * Milliseconds.SECOND, s3TimeKeyLevel: S3TimeKeyLevel.HOUR });
            const clonedSystemOperationReports = { ...systemOperationRecords };
            const timeFilteredReports = Object.fromEntries(Object.entries(clonedSystemOperationReports).filter(([key, report]) => {
                const timeKeyPrefix = key.replace(timeKeyRegexOut, '');
                debug && console.log({ timeKeyPrefix, fromTimeKeyPrefix, toTimeKeyPrefix });
                return timeKeyPrefix >= fromTimeKeyPrefix && timeKeyPrefix <= toTimeKeyPrefix;
            }));
            for (const [Key, report] of Object.entries(timeFilteredReports)) {
                if (!report) {
                    const { err, data } = await getObjectFromS3({ Key, bucketIdentifier: Bucket });
                    if (err) {
                        debug && console.log('Error getting object', err.message);
                        dispatch({ error: `Could not retrieve record(s)` });
                    }
                    if (data) {
                        timeFilteredReports[Key] = data;
                        clonedSystemOperationReports[Key] = timeFilteredReports[Key];
                        dispatch({ timeFilteredReports, systemOperationRecords: clonedSystemOperationReports, error: null });
                    }
                }
            }
        }
        catch (e) {
            debug && console.log('Error getting keys', e.message);
        }
    };
    useEffect(() => {
        getKeys();
    }, []);
    useEffect(() => {
        filterKeys();
    }, [fromDateValue, toDateValue]);
    const systemReportKeysPresent = !!systemOperationRecords && Object.keys(systemOperationRecords).length > 0;
    useEffect(() => {
        filterKeys();
    }, [systemReportKeysPresent]);
    const lastTimestamp = systemOperationRecords ? SystemReportTimeKeyToTimestamp(Object.keys(systemOperationRecords)[0]) : null;
    const lastChecked = lastTimestamp ? new Date(lastTimestamp).toLocaleString() : null;
    return (_jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Typography, { variant: "h5", children: "System Operation Report" }), error && (_jsx(Alert, { severity: "error", sx: { paddingBlock: 0 }, children: error })), lastChecked && (_jsxs(Alert, { severity: "info", sx: { paddingBlock: 0 }, children: ["Last check: ", lastChecked] }))] }), TimePeriodSelector, _jsx(MultiReportStatus, { systemReports: timeFilteredReports }), _jsxs(Stack, { children: [_jsxs(IoTVAccordion, { children: [_jsx(IoTVAccordionSummary, { children: "All" }), _jsx(IoTVAccordionDetails, { children: _jsx(JsonViewer, { value: systemOperationRecords }) })] }), _jsxs(IoTVAccordion, { children: [_jsx(IoTVAccordionSummary, { children: "Filtered" }), _jsx(IoTVAccordionDetails, { children: _jsx(JsonViewer, { value: timeFilteredReports }) })] })] })] }));
};
